import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n";
import Seo from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_ORDER = ["Africa", "Americas", "Asia", "Europe", "Oceania", "Antarctic"];

export default function CountriesDirectory() {
  const { tr } = useI18n();
  const [countries, setCountries] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/countries`).then((r) => setCountries(r.data)).catch(() => setCountries([]));
  }, []);

  if (!countries) {
    return (
      <div data-testid="countries-loading" className="min-h-[60vh] flex items-center justify-center gap-3 text-[#2563EB]">
        <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Loading the world…")}</span>
      </div>
    );
  }

  const query = q.trim().toLowerCase();
  const filtered = countries.filter((c) =>
    !query || c.name.toLowerCase().includes(query) || c.official.toLowerCase().includes(query) ||
    (c.capital || "").toLowerCase().includes(query) || c.cca2.toLowerCase() === query);
  const byRegion = {};
  filtered.forEach((c) => {
    const r = c.region || "Other";
    (byRegion[r] = byRegion[r] || []).push(c);
  });
  const regions = [...REGION_ORDER.filter((r) => byRegion[r]), ...Object.keys(byRegion).filter((r) => !REGION_ORDER.includes(r))];

  return (
    <div data-testid="countries-directory" className="pb-24">
      <Seo
        title="All Countries — WorldPulse.pro"
        description="Explore every country and territory on Earth — geography, history, economy, culture, food, travel and communities. The geographic home of the Social Pulse network."
        canonical="https://worldpulse.pro/countries"
      />
      <div className="py-12 lg:py-16" style={{ background: "linear-gradient(135deg, #EAF4FD 0%, #F6FAFF 45%, #E8F7F0 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <span className="wp-eyebrow">{tr("The Country Layer")}</span>
          <h1 data-testid="countries-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F2557] tracking-tight mt-3">
            {tr("Every country on Earth.")}
          </h1>
          <p className="text-base md:text-lg text-[#475569] mt-4 max-w-2xl">
            {tr("250 countries and territories with verified identities — each becoming a living national portal with facts, culture, food, travel and communities.")}
          </p>
          <div className="mt-7 flex items-center bg-white rounded-2xl shadow-lg shadow-blue-100 border border-[#DCE8F7] p-2 max-w-xl">
            <Search size={18} className="text-[#2563EB] ml-3 shrink-0" />
            <input
              data-testid="countries-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr("Search by country, capital or ISO code…")}
              className="flex-1 px-3 py-2.5 text-[#0F2557] placeholder:text-[#94A3B8] focus:outline-none text-sm min-w-0 bg-transparent"
            />
            <span className="text-xs font-bold text-[#64748B] px-3">{filtered.length}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {regions.map((region) => (
          <div key={region} className="mt-12">
            <h2 className="text-xl font-extrabold text-[#0F2557] flex items-center gap-3" data-testid={`countries-region-${region.toLowerCase()}`}>
              {tr(region)} <span className="text-xs font-bold text-[#64748B] bg-[#F1F6FD] rounded-full px-3 py-1">{byRegion[region].length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4">
              {byRegion[region].map((c) => (
                <Link key={c.slug} to={`/country/${c.slug}`} data-testid="country-directory-card"
                  className="flex items-center gap-3 border border-[#E5EDF7] rounded-2xl px-4 py-3 hover:border-[#2563EB] hover:bg-[#F6FAFF] transition-colors group">
                  <span className="text-2xl">{c.flag}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-extrabold text-[#0F2557] group-hover:text-[#2563EB] truncate">{c.name}</span>
                    <span className="block text-[0.65rem] text-[#64748B] font-semibold truncate">
                      {c.capital || "—"}{c.population ? ` · ${(c.population / 1e6).toFixed(1)}M` : ""}
                    </span>
                  </span>
                  {c.enriched && <CheckCircle2 size={14} className="text-[#16A34A] shrink-0" title="Full portal ready" />}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
